import type { AppSection } from '@hofsync/design-system';

/**
 * Map app sections to their default routes. Single source of truth for
 * both `ProtectedLayout`'s click handler and the test below. The
 * mirror direction (`routeToSection`) stays in `ProtectedLayout.tsx`
 * because it carries some redirects (`/settings`/`/connections` →
 * `documents`) that aren't reversible from a section id.
 */
export const sectionToRoute: Record<AppSection, string> = {
    pruefstatus: '/pruefstatus',
    workspace: '/workspace',
    'my-farm': '/farm',
    documents: '/documents',
    history: '/history',
    admin: '/admin',
    apps: '/apps',
    agent: '/agent',
};

export interface SectionNavigateResult {
    /**
     * Target pathname to navigate to (no query string — the bare route,
     * which lands directly in the section's default lens since the
     * starting pages were retired in #2108: /documents renders the
     * "Alle Dokumente" lens, /farm canonicalises to the first
     * registry-served lens).
     */
    target: string;
    /**
     * When set to `replace`, the router should call
     * `navigate(target, { replace: true })`. Used for the
     * active-section reset gesture so we don't pollute the history
     * stack with consecutive bare-path entries.
     */
    options?: { replace: true };
}

/**
 * Resolve what the left focus-panel section click should navigate to.
 *
 * Behaviour matrix (#951, targets re-pointed by #2108):
 *   1. Click on a different section → navigate to that section's route (push).
 *   2. Click on the **active** section with query params present → navigate
 *      to the bare route with `replace: true`. This drops `?lense=…` /
 *      `?category=…` / `?sub=…` etc. so the user resets to the section's
 *      default lens (#2108 — formerly the starting page).
 *   3. Click on the active section with no query params (already at the
 *      bare default) → returns `null`. The caller should no-op
 *      rather than pushing a duplicate identical entry into history.
 */
export function resolveSectionNavigate(
    section: AppSection,
    currentPathname: string,
    currentSearch: string,
): SectionNavigateResult | null {
    const target = sectionToRoute[section];
    if (!target) return null;

    if (currentPathname !== target) {
        return { target };
    }

    // Same section. Reset only if there's deep state to clear.
    const hasSearch = currentSearch && currentSearch !== '' && currentSearch !== '?';
    if (hasSearch) {
        return { target, options: { replace: true } };
    }

    return null;
}
